// /admin/v2/settings — study settings (BRIEFS C3.4 with the keys of C1.2, SPEC §7).
// "Everyone" edits the general values; choosing a student edits values for him only
// (his own value wins over the general one; "Use the general value" removes his).
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { errText, v2SettingClear, v2SettingSave, v2Settings } from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';
import StudentPicker from './StudentPicker';

function Row({ item, studentId, onSaved, onError }) {
  const mine = studentId ? item.student : item.general;
  const [val, setVal] = useState(String(mine ?? item.effective));
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(String(mine ?? item.effective)); }, [mine, item.effective]);
  const changed = val.trim() !== String(mine ?? item.effective);

  const save = async () => {
    setBusy(true);
    try { await v2SettingSave(studentId, item.key, val.trim()); onSaved('Saved.'); }
    catch (x) { onError(errText(x, 'Could not save.')); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await v2SettingClear(studentId, item.key); onSaved('Now using the general value.'); }
    catch (x) { onError(errText(x, 'Could not change it.')); }
    finally { setBusy(false); }
  };

  const where = studentId
    ? (item.student != null ? 'His own value' : item.general != null ? 'General value' : 'Built-in value')
    : (item.general != null ? 'General value' : 'Built-in value');
  return (
    <tr data-testid={`v2set-row-${item.key}`}>
      <td style={{ width: '38%', minWidth: 260 }}>
        <div style={{ fontWeight: 600 }}>{item.label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{item.help}</div>
      </td>
      <td>
        <input className="form-input" value={val} onChange={(e) => setVal(e.target.value)}
          data-testid={`v2set-input-${item.key}`}
          inputMode={item.kind === 'int' ? 'numeric' : undefined}
          style={{ width: item.kind === 'email' ? 240 : 110, maxWidth: '100%', display: 'inline-block', marginRight: 8 }} />
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{item.unit}</span>
      </td>
      <td style={{ fontSize: 13, wordBreak: 'break-word' }}>
        <div>Used now: <strong>{String(item.effective)}</strong></div>
        <div style={{ color: 'var(--text-muted)' }}>{where} · built-in {String(item.default)}</div>
      </td>
      <td>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
        <button className="btn btn-primary btn-sm" disabled={busy || !changed} onClick={save}
          style={{ opacity: busy || !changed ? 0.45 : 1 }}
          data-testid={`v2set-save-${item.key}`}>Save</button>
        {studentId && item.student != null && (
          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={clear} style={{ whiteSpace: 'nowrap' }}
            data-testid={`v2set-clear-${item.key}`}>Use the general value</button>
        )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminV2Settings() {
  const [params, setParams] = useSearchParams();
  const student = params.get('student') || '';
  const [msg, setMsg] = useState(null);
  const q = useQuery({ queryKey: ['v2a', 'settings', student], queryFn: () => v2Settings(student || undefined) });
  const items = (q.data && q.data.items) || [];

  return (
    <div>
      <V2Tabs group="student" title="Study settings" intro="Numbers the app uses for study time, the streak, the daily goal and the daily report. Change them for everyone, or choose a student to set his own." />
      <Msg msg={msg} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14 }}>Settings for:</span>
        <StudentPicker value={student} allowEmpty emptyLabel="Everyone (general values)" testId="v2set-student"
          onChange={(id) => { setMsg(null); setParams(id ? { student: id } : {}); }} />
      </div>
      {student && q.data && q.data.student && (
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 10 }}>
          Values saved here are for {q.data.student.name} only. Where he has no own value, the general value is used.
        </div>
      )}
      {q.isLoading ? <div>Loading…</div> : q.isError ? (
        <div className="card" style={{ padding: 18, color: 'var(--danger)' }}>{errText(q.error, 'Could not load the settings.')}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }} data-testid="v2set-list">
            <thead><tr><th>Setting</th><th>Value</th><th>Now</th><th /></tr></thead>
            <tbody>
              {items.map((it) => (
                <Row key={`${student}-${it.key}`} item={it} studentId={student || null}
                  onSaved={(text) => { setMsg({ kind: 'ok', text }); q.refetch(); }}
                  onError={(text) => setMsg({ kind: 'err', text })} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
